import { Box, Button, CircularProgress, IconButton, Popover, Stack } from '@mui/material';
import { Fragment, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import alertify from 'alertifyjs';

import { useAPI } from 'hooks/useAPI';
import { ISurveyMetadata } from '../../models/interfaces/ISurveyMetadata';
import { SeismicLineSelect } from './SeismicLineSelect';
import { useGetAll2DLines } from './api/useSeismicLineController';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { SeismicProductSelect } from './SeismicProductSelect';
import { useTileSize } from 'features/seismic/hooks/useTileSize';
import { Survey2DLines } from 'features/seismic/models/interfaces/I2DLines';
import { Product } from 'features/seismic/models/interfaces/IProducts';
import { ProductBaseInfo } from 'features/seismic/models/types/ProductBaseInfo';
import { UseAttributesStore } from 'features/seismic/stores/useAttributesStore';
import { IColorbar } from 'features/seismic/models/interfaces/IColorbar';
import { useGetLineProducts } from '../attributes/api/useSeismicAttributesController';

interface IMetadataResponse {
    Metadata: ISurveyMetadata;
    Resolutions: number[];
    Colorbar: IColorbar;
}

export function SeismicLine2DSelector() {
    const { setSurveyMetadata, setVolumeToken, volumeToken, isReLoadingLineGeoemtry, setIsReLoadingLineGeoemtry, setLines2D, setVolumeTokenOverlay,
        setVolumeTokenSwipe, setSurveyMetadataOverlay, setSurveyMetadataSwipe, setIsNavbarLoading, setGain, setColorbar, setIsLoading, surveyMetadata,
        setWarningMessage } = useSeismicStore(state => ({
        volumeToken: state.volumeToken,
        setVolumeToken: state.setVolumeToken,
        setSurveyMetadata: state.setSurveyMetadata,
        setSurveyMetadataOverlay: state.setSurveyMetadataOverlay,
        setSurveyMetadataSwipe: state.setSurveyMetadataSwipe,
        setVolumeTokenOverlay: state.setVolumeTokenOverlay,
        setVolumeTokenSwipe: state.setVolumeTokenSwipe,
        isReLoadingLineGeoemtry: state.isReLoadingLineGeometry,
        setIsReLoadingLineGeoemtry: state.setIsReLoadingLineGeoemtry,
        setLines2D: state.setLines2D,
        setIsNavbarLoading: state.setNavbarLoading,
        setColorbar: state.setColorbar,
        setGain: state.setAbsoluteGain,
        setIsLoading: state.setNavbarLoading,
        surveyMetadata: state.surveyMetadata,
        setWarningMessage: state.setWarningMessage,
    }));
    const { isLoading: loadingLines, data: dataLines, error: errorOnLoadingLines } = useGetAll2DLines(volumeToken, 4326);

    const { selectedProductName, overlaySelectedProductName, swipeSelectedProductName, setSelectedLineName, availableProducts, setSelectedProductName, selectedLineName } = UseAttributesStore();

    const previousSelectedLineRef = useRef<string|null>(selectedLineName);

    const { width: tileWidthDefault } = useTileSize();
    const urlApi = '/Seismic/Volume/Metadata';
    const { execute: executeMainMetadata, isLoading: isLoading } = useAPI<IMetadataResponse>(urlApi, (error) => {
        if (error.message.indexOf('Request failed with status code 401') > -1) {
            alertify.alert('Authentication error');
        }
        else if (error.message.indexOf('Network Error') > -1) {
            alertify.alert('Network error');
        }
        else {
            alertify.alert('An error ocurred');
        }
    });

    const handleLineSelection = useCallback(async (selectedLine: Survey2DLines|undefined|null) => {
        if (!!selectedLine) {
            setIsLoading(true);
            previousSelectedLineRef.current = selectedLineName;
            setSelectedLineName(selectedLine.LineName);
        }
    }, [selectedLineName, setIsLoading, setSelectedLineName]);

    const handleSwipeLineSelection = useCallback(async () => {
        const selectedProduct = availableProducts.find(product => product.Product === swipeSelectedProductName);
        if (!!swipeSelectedProductName && !!selectedProduct && selectedProduct.VolumeToken.toString() !== volumeToken?.toString()) {
            setVolumeTokenSwipe(selectedProduct.VolumeToken.toString());
        }
        else {
            setVolumeTokenSwipe('-');
            setSurveyMetadataSwipe(null);
        }
    }, [availableProducts, setSurveyMetadataSwipe, setVolumeTokenSwipe, swipeSelectedProductName, volumeToken]);

    const handleOverlayLineSelection = useCallback(async () => {
        const selectedProduct = availableProducts.find(product => product.Product === overlaySelectedProductName);
        if (!!overlaySelectedProductName && !!selectedProduct && selectedProduct.VolumeToken.toString() !== volumeToken?.toString()) {
            setVolumeTokenOverlay(selectedProduct.VolumeToken.toString());
        }
        else {
            setVolumeTokenOverlay('-');
            setSurveyMetadataOverlay(null);
        }
    }, [availableProducts, overlaySelectedProductName, setSurveyMetadataOverlay, setVolumeTokenOverlay, volumeToken]);

    useEffect(() => {
        if (dataLines) {
            setLines2D(dataLines);
            const selectedLine = dataLines.find(line => line.LineName === surveyMetadata?.LineName);
            if (!!selectedLine) {
                setSelectedLineName(selectedLine?.LineName);
            }
        }
    }, [dataLines, surveyMetadata]);

    useEffect(() => {
        const products = availableProducts;
        let selectedProduct = products.find(product => product.Product === selectedProductName);

        if (!selectedProduct) {
            if (products.length > 0) {
                selectedProduct = products[0];
                setWarningMessage(`The product '${selectedProductName}' is unavailable for line '${selectedLineName}', so '${selectedProduct.Product}' has been selected instead.`);
            }
            else if (!!selectedLineName) {
                setWarningMessage(`There are no products available for line ${selectedLineName}.`);
                if (!!previousSelectedLineRef.current) {
                    setSelectedLineName(previousSelectedLineRef.current);
                }
                setIsLoading(false);
                return;
            }
        }
        if (!!selectedProductName && !!selectedProduct && selectedProduct.VolumeToken.toString() !== volumeToken?.toString()) {
            setIsReLoadingLineGeoemtry(true);
            setIsLoading(true);
            executeMainMetadata({
                params: {
                    filePath: '',
                    volumeToken: selectedProduct.VolumeToken,
                    tileWidth: tileWidthDefault,
                    screenHeight: 893,
                    volumeIdentifier: 'volumeId',
                    includePercentageCalc: true,
                }
            }).then(response => {
                if (response) {
                    const metadata = response.Metadata;
                    //metadata.VolumeToken = selectedProduct.Id;
                    setSurveyMetadata(metadata);
                    setVolumeToken(metadata.VolumeToken);
                    setGain({ max: metadata.MaxSampleValue, min: metadata.MinSampleValue });
                    setColorbar(response.Colorbar);
                }
                setIsReLoadingLineGeoemtry(false);
                const promises = [
                    handleSwipeLineSelection(),
                    handleOverlayLineSelection()
                ];
                Promise.allSettled(promises).then(() => setIsLoading(false));
            });
        }
        else if (!!selectedProductName && !!surveyMetadata?.ProductName) {
            setSelectedProductName(surveyMetadata.ProductName);
            setIsLoading(false);
        }
    }, [availableProducts]);

    useEffect(() => {
        const attributes = dataLines;
        if (!!attributes && !!surveyMetadata) {
            let lines = attributes.sort((a, b) => {
                const nameA = a.LineName.toUpperCase();
                const nameB = b.LineName.toUpperCase();

                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });

            let selectedLine = lines.find(line => !!surveyMetadata.LineName && line.LineName === surveyMetadata.LineName); //lines.find(el => el.Products.findIndex(x => x.Id === parseInt(volumeToken)) > -1);

            if (selectedLine) {
                setSelectedLineName(selectedLine.LineName);
                //selected(selectedLine);
            }
            else {
                setSelectedLineName(lines[0].LineName);
                handleLineSelection(lines[0]);
            }
        }
    }, [dataLines]);

    return (
        <SeismicLineSelect attributes={dataLines} onChange={handleLineSelection} selectedLine={selectedLineName} />
    );
}